import { PropTypes } from 'prop-types';
import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import { injectIntl } from 'react-intl';
import { useIntercom } from 'react-use-intercom';
import {
  sendMessage,
  toggleChatbotVisibility,
  clearMessages,
} from '../../redux/actions/chatbot';
import './FloatingChat.scss';

const FloatingChat = ({
  isVisible,
  messages,
  loading,
  sendMessage,
  toggleVisibility,
  clearMessages,
  intl,
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const messagesEndRef = useRef(null);
  const { boot, show, showNewMessages } = useIntercom();
  const SEND_CHAT_CONTEXT_TO_INTERCOM = true;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      sendMessage(inputMessage.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleOpenIntercom = () => {
    toggleVisibility();
    
    if (SEND_CHAT_CONTEXT_TO_INTERCOM && messages.length > 0) {
      const chatHistory = messages
        .map((msg) => {
          const time = new Date(msg.timestamp).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          });
          const sender = msg.sender === 'user' ? 'Usuario' : 'Aries Bot';
          return `[${time}] ${sender}: ${msg.text}`;
        })
        .join('\n');

      boot({
        hideDefaultLauncher: true,
        customAttributes: {
          aries_chat_history: chatHistory,
        },
      });

      show();
      setTimeout(() => {
        showNewMessages();
      }, 400);

      console.log('Chat de Aries cerrado. Intercom abierto con contexto:', chatHistory);
    } else {
      boot({ hideDefaultLauncher: true });
      show();
      setTimeout(() => {
        showNewMessages();
      }, 400);
      console.log('Chat de Aries cerrado. Intercom abierto sin contexto.');
    }
  };

  if (!isVisible) return null;

  return (
    <div className="floating-chat">
      <div className="chat-header">
        <div className="chat-title">
          <i className="material-icons">support_agent</i>
          <span>
            <FormattedMessage
              id="chatbotTitle"
              defaultMessage="Aries Chatbot"
            />
          </span>
        </div>
        <div className="chat-actions">
          <button
            className="clear-btn"
            onClick={clearMessages}
            title="Limpiar conversación"
          >
            <i className="material-icons">clear_all</i>
          </button>
          <button
            className="close-btn"
            onClick={toggleVisibility}
            title="Cerrar chat"
          >
            <i className="material-icons">close</i>
          </button>
        </div>
      </div>

      <div className="chat-messages">
        {messages.length === 0 && (
          <div className="welcome-message">
            <div className="bot-message">
              <div className="message-content">
                <FormattedMessage
                  id="chatInitialMessage"
                  defaultMessage="Hi, I am here to help you. How can I assist you today?"
                />
              </div>
            </div>
          </div>
        )}

        {messages.map((message) => {
          const isNoAnswer = message.text === 'NO_ANSWER_FROM_KNOWLEDGE_BASE';
          const isNegativeFallback = message.text === 'NEGATIVE_FEEDBACK_FROM_USER';
          const displayText = isNoAnswer || isNegativeFallback
            ? intl.formatMessage({
                id: 'chatNoAnswerFromKnowledgeBase',
              })
            : message.text;

          return (
            <div
              key={message.id}
              className={`message ${message.sender}-message`}
            >
              <div className="message-content">
                {displayText}
                {(isNoAnswer || isNegativeFallback) && (
                  <div style={{ marginTop: '10px' }}>
                    <button
                      onClick={handleOpenIntercom}
                      style={{
                        background: '#0066ff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <i className="material-icons" style={{ fontSize: '18px' }}>
                        support_agent
                      </i>
                      <FormattedMessage
                        id="chatContactSupport"
                        defaultMessage="Contact Support"
                      />
                    </button>
                  </div>
                )}
              </div>
              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="bot-message">
            <div className="message-content typing">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <form className="chat-input" onSubmit={handleSendMessage}>
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={intl.formatMessage({ id: 'chatMessagePlaceholder' })}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading || !inputMessage.trim()}
          className="send-btn"
        >
          <i className="material-icons">send</i>
        </button>
      </form>
    </div>
  );
};

FloatingChat.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
      sender: PropTypes.string.isRequired,
      timestamp: PropTypes.string.isRequired,
    })
  ).isRequired,
  loading: PropTypes.bool.isRequired,
  sendMessage: PropTypes.func.isRequired,
  toggleVisibility: PropTypes.func.isRequired,
  clearMessages: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  isVisible: state.chatbot.isVisible,
  messages: state.chatbot.messages,
  loading: state.chatbot.loading,
});

const mapDispatchToProps = {
  sendMessage,
  toggleVisibility: toggleChatbotVisibility,
  clearMessages,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(FloatingChat));
